import { ThemeProvider } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import userAPI from '../../api/wave/users';
import Loader from '../../store/loader';
import User from '../../store/user';
import { theme } from '../../utils/themeProvider';
import { PrimaryButton } from '../Buttons/Buttons';
import GDPRCheckbox from '../GDPRCheckbox/GDPRCheckbox';

import styles from './RegisterForm.module.scss';

const DEFAULT_ERROR_MESSAGE = 'Κάτι πήγε στραβά. Παρακαλώ προσπαθήστε ξανά.';
const REQUIRED_FIELD = 'Υποχρεωτικό πεδίο';

export default function RegisterForm({ setVerifyToken, successCallback = () => {}, hasLink = true, isDigital }) {
  const [hasAcceptTerms, setHasAcceptTerms] = useState(false);
  const [registerError, setRegisterError] = useState('');
  const router = useRouter();
  const errorMessages = {
    email: {
      required: REQUIRED_FIELD,
      pattern: 'Μη έγκυρο email',
    },
    phone: {
      required: REQUIRED_FIELD,
      pattern: 'Μη έγκυρο κινητό τηλέφωνο',
    },
  };

  const { control, handleSubmit } = useForm({
    mode: 'onBlur',
  });

  function toggleTerms() {
    setHasAcceptTerms(!hasAcceptTerms);
  }

  function onSubmitForm(data) {
    setRegisterError('');
    registerSubmit(data);
  }

  async function registerSubmit(_data) {
    const user = User.getValue();
    const { firstName, surname, email, phone } = _data;
    Loader.addEvent('SIGN_UP');
    try {
      const _cardId = user?.cardId || undefined;
      const data = {
        firstName,
        lastName: surname,
        cardId: _cardId,
        email,
        mobile: { phone },
        registrationAppID: process.env.appId,
        termsAccepted: true,
      };

      const res = await userAPI.singUp(data);
      setVerifyToken(res.payload.verification.verificationToken);
      User.update({
        maskedMobile: res.payload.verification.mobile,
      });
      setRegisterError('');
      Loader.removeEvent('SIGN_UP');
      successCallback();
    } catch (error) {
      setRegisterError(error?.error?.userMessage || DEFAULT_ERROR_MESSAGE);
      Loader.removeEvent('SIGN_UP');
    }
  }

  function onClickRedirect() {
    if (isDigital) {
      router.push({
        pathname: '/login',
        query: { isDigital: 'digital' },
      });
    } else {
      router.push({
        pathname: '/login',
      });
    }
  }

  return (
    <form noValidate className={styles.fullWidth} onSubmit={handleSubmit(onSubmitForm)}>
      <ThemeProvider theme={theme}>
        <Controller
          control={control}
          name="firstName"
          defaultValue=""
          rules={{
            required: true,
          }}
          render={({ field: { ref, onChange, ...field }, fieldState: { error } }) => (
            <TextField
              {...field}
              label="Όνομα"
              variant="outlined"
              error={error !== undefined}
              helperText={error && REQUIRED_FIELD}
              onChange={onChange}
              fullWidth
              required
              className={styles.formField}
            />
          )}
        />
        <Controller
          control={control}
          name="surname"
          defaultValue=""
          rules={{
            required: true,
          }}
          render={({ field: { ref, onChange, ...field }, fieldState: { error } }) => (
            <TextField
              {...field}
              label="Επώνυμο"
              variant="outlined"
              error={error !== undefined}
              helperText={error && REQUIRED_FIELD}
              onChange={onChange}
              fullWidth
              required
              className={styles.formField}
            />
          )}
        />
        <Controller
          control={control}
          name="email"
          defaultValue=""
          rules={{
            required: true,
            pattern:
              // eslint-disable-next-line max-len
              /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
          }}
          render={({ field: { ref, onChange, ...field }, fieldState: { error } }) => (
            <TextField
              {...field}
              label="Email"
              type="email"
              variant="outlined"
              onChange={onChange}
              fullWidth
              required
              error={error !== undefined}
              helperText={error && errorMessages.email[error.type]}
              className={styles.formField}
            />
          )}
        />
        <Controller
          control={control}
          name="phone"
          defaultValue=""
          rules={{
            required: true,
            pattern: /^(\+30|0030)?69\d{8}$/,
          }}
          render={({ field: { ref, onChange, ...field }, fieldState: { error } }) => (
            <TextField
              {...field}
              label="Κινητό τηλέφωνο"
              variant="outlined"
              onChange={onChange}
              fullWidth
              required
              error={error !== undefined}
              helperText={error && errorMessages.phone[error.type]}
              className={styles.formField}
              inputProps={{
                inputMode: 'numeric',
                pattern: '[0-9]*',
              }}
            />
          )}
        />
        <GDPRCheckbox onClickCheckbox={toggleTerms} />
        <div className={styles.primaryButtonContainer}>
          <PrimaryButton type="submit" data-cy="continue" style={{ width: '100%' }} disabled={!hasAcceptTerms}>
            Συνέχεια
          </PrimaryButton>
          <p className={styles.singUpError}>{registerError}</p>
        </div>
        {hasLink && (
          <div className={styles.loginText}>
            Έχεις ήδη λογαριασμό;
            <div onClick={onClickRedirect}>
              <a className={styles.loginLink}> Σύνδεση</a>
            </div>
          </div>
        )}
      </ThemeProvider>
    </form>
  );
}
